// /track — the one place a buyer checks on a request.
//
// Logged-in customer: their organization's requests and proformas listed
// directly (GET /customer/requests), newest first. Each row links to
// /proforma/:trackingToken — the one canonical tracking page (status,
// timeline, proforma table + download, line-by-line review, notes). No
// reference number to type.
//
// Guest (no account, no tracking link): the reference-number + email lookup
// (POST /track/lookup) — the one credential an anonymous submitter has. A
// match navigates to that same /proforma/:token page.
//
// Company / contact / notification-preference editing moved OUT of here to
// /account. Old /account request routes redirect back here (App.jsx).

import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../context/AuthContext.jsx';
import { useLanguage } from '../context/LanguageContext.jsx';
import PortalShell from '../components/PortalShell.jsx';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import { Spinner } from 'shared/components/Feedback/index.js';
import { useMyRequests } from '../hooks/useAccount.js';
import { useTrackingLookup } from '../hooks/useProformaTracking.js';
import { mapApiErrorId } from '../utils/errorMapping.js';
import { formatDate } from '../utils/formatDate.js';
import styles from './TrackRequestPage.module.css';

const LOOKUP_KNOWN_CODES = ['TRACKING_TOKEN_NOT_FOUND', 'TRACKING_LOOKUP_RATE_LIMITED'];

function CustomerRequestList() {
  const { t, lang } = useLanguage();
  const { data: requests, isLoading, isError, error } = useMyRequests();

  return (
    <>
      <h1 className={styles.title}>{t('portal.track.myRequestsTitle')}</h1>
      <p className={styles.lede}>{t('portal.track.myRequestsLede')}</p>

      {isLoading && <LoadingState />}
      {isError && !isLoading && <ErrorState messageId="portal.account.errorBody" error={error} />}

      {!isLoading && !isError && (!requests || requests.length === 0) && (
        <p className={styles.empty}>{t('portal.account.empty')}</p>
      )}

      {!isLoading && !isError && requests && requests.length > 0 && (
        <ul className={styles.list}>
          {requests.map((request) => (
            <li key={request.id}>
              <Link to={`/proforma/${request.trackingToken}`} className={styles.row}>
                <span className={styles.ids}>
                  <span className={`${styles.refNo} tabularNums`}>
                    {request.proforma_no || request.ref_no}
                  </span>
                  {request.proforma_no && (
                    <span className={`${styles.subRef} tabularNums`}>{request.ref_no}</span>
                  )}
                </span>
                <span className={styles.status}>
                  {t(`portal.buyerStatus.${request.buyerStatus}`)}
                </span>
                <span className={`${styles.date} tabularNums`}>
                  {formatDate(request.created_at, lang) || '—'}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function GuestLookup() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { refNo: '', email: '' } });
  const lookup = useTrackingLookup();

  const onSubmit = (values) => {
    lookup.mutate(values, {
      onSuccess: (data) => navigate(`/proforma/${data.trackingToken}`),
    });
  };

  const errorId = lookup.error ? mapApiErrorId(lookup.error, LOOKUP_KNOWN_CODES) : null;

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <h1 className={styles.title}>{t('portal.track.title')}</h1>
      <p className={styles.lede}>{t('portal.track.guestLede')}</p>

      <label className={styles.field}>
        <span className={styles.label}>{t('portal.track.refNoLabel')}</span>
        <input
          type="text"
          autoComplete="off"
          className={`${styles.input} tabularNums`}
          {...register('refNo', { required: true })}
        />
        {errors.refNo && <span className={styles.fieldError}>{t('error.VALIDATION_ERROR')}</span>}
      </label>

      <label className={styles.field}>
        <span className={styles.label}>{t('portal.track.emailLabel')}</span>
        <input
          type="email"
          autoComplete="email"
          className={styles.input}
          {...register('email', { required: true })}
        />
        {errors.email && <span className={styles.fieldError}>{t('error.VALIDATION_ERROR')}</span>}
      </label>

      {errorId && <ErrorState messageId={errorId} error={lookup.error} />}

      <button type="submit" className={styles.submitButton} disabled={lookup.isPending}>
        {lookup.isPending && <Spinner />} {t('portal.track.lookupButton')}
      </button>

      <p className={styles.altPrompt}>
        {t('portal.track.haveAccountPrompt')}{' '}
        <Link to="/login" className={styles.inlineLink}>
          {t('portal.login.title')}
        </Link>
      </p>
    </form>
  );
}

export default function TrackRequestPage() {
  const { user } = useAuth();
  const isCustomer = Boolean(user) && user.role === 'customer';

  return (
    <PortalShell width={isCustomer ? 'medium' : 'narrow'}>
      {isCustomer ? <CustomerRequestList /> : <GuestLookup />}
    </PortalShell>
  );
}
